import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { parseFormattedNote } from '../utils/formatProgressNote';
import { formatPatientName } from '../utils/formatName';
import PatientARClaimsTable from '../components/PatientARClaimsTable';
import './AppointmentsPage.css';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateInputBounds';

const API_URL = process.env.REACT_APP_API_URL || '';

const apptStatusColors = {
  Confirmed: '#2E9B6B',
  Scheduled: '#718096',
  Unconfirmed: '#D4AD5C',
  'Checked In': '#2D4470',
  'No Show': '#C53030',
  'No-show': '#C53030',
  'No-Show': '#C53030',
};

function todayInTimezone(timeZone) {
  // en-CA's date format is YYYY-MM-DD, which matches our wire format.
  // Falls back to local browser date when no TZ is supplied.
  const opts = timeZone ? { timeZone } : {};
  return new Date().toLocaleDateString('en-CA', opts);
}

function formatTime(isoString, timeZone) {
  if (!isoString) return '';
  try {
    const dt = new Date(isoString);
    const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (timeZone) opts.timeZone = timeZone;
    return dt.toLocaleTimeString('en-US', opts);
  } catch {
    return isoString;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

function formatEligDate(isoStr) {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('T')[0].split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return months[parseInt(m, 10) - 1] + '. ' + parseInt(d, 10);
}

// 4-state eligibility icon — matches the daily email's visual language so
// both surfaces use the same algorithm + same colors. icon_state comes from
// classify_eligibility_check on the backend.
//   good      = green check (Active)
//   bad       = red X       (Rejected, Submission Error)
//   uncertain = orange ?    (Stale, No check on file, Unknown)
//   na        = gray dash   (No insurance / not applicable)
function EligIcon({ state }) {
  if (state === 'good') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#16a34a"/>
        <path d="M7 12.5l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (state === 'bad') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#dc2626"/>
        <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (state === 'na') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#6b7280"/>
        <path d="M7 12h10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    );
  }
  // uncertain — orange ?
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#d97706"/>
      <text x="12" y="17" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="700" fontFamily="Arial, sans-serif">?</text>
    </svg>
  );
}

// Demo eligibility: the payer, what it said, and (only when it refused) why —
// short enough to scan down the column. Never the raw clearinghouse text.
function EligStack({ elig }) {
  // Outcome first, payer under it; the reason (if any) waits for a hover.
  return (
    <div className="elig-stack" title={elig.reason || undefined}>
      <span className={`elig-stack-outcome${elig.reason ? ' has-reason' : ''}`}>
        <EligIcon state={elig.outcome_icon} />
        {elig.outcome}
      </span>
      <span className="elig-stack-payer">{elig.payer_name || 'Unknown payer'}</span>
    </div>
  );
}

function NoteIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}

// Green check on the note's upper-right corner once a claim exists for the visit.
function ClaimCheck() {
  return (
    <span className="claim-check" title="Claim created">
      {/* White ring so the check reads where it overlaps the note icon */}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10.5" fill="#16a34a" stroke="#fff" strokeWidth="3"/>
        <path d="M7.5 12.5l3 3 6-6" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );
}

function AppointmentsPage({ selectedPractice }) {
  const [selectedDate, setSelectedDate] = useState(() =>
    todayInTimezone(selectedPractice?.timezone)
  );
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [checkingElig, setCheckingElig] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastSyncRange, setLastSyncRange] = useState(null);
  const [enabled, setEnabled] = useState(null); // null = unknown, true/false = checked
  const [activeTypeFilters, setActiveTypeFilters] = useState(new Set()); // empty = show all
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [noteModal, setNoteModal] = useState(null); // { patientName, note } or null
  // { patientName, claims (null while loading), error } or null
  const [balanceModal, setBalanceModal] = useState(null);

  const typeDropdownRef = useRef(null);
  const practiceId = selectedPractice?.id;
  // The demo practice (emr_system = "demo"): fake people and seeded checks,
  // shown in the same day view as every other practice plus a balance column.
  const isDemo = Boolean(selectedPractice?.is_demo);
  const [demoNoticeOpen, setDemoNoticeOpen] = useState(true);

  const appointmentTypes = useMemo(() => {
    const counts = {};
    appointments.forEach((a) => {
      const t = a.appointment_type || 'Unknown';
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [appointments]);

  const filteredAppointments = useMemo(() => {
    if (activeTypeFilters.size === 0) return appointments;
    return appointments.filter((a) => activeTypeFilters.has(a.appointment_type || 'Unknown'));
  }, [appointments, activeTypeFilters]);

  const toggleTypeFilter = useCallback((type) => {
    setActiveTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const loadAppointments = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/appointments?practice_id=${practiceId}&date=${selectedDate}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setEnabled(data.enabled !== false);
        setAppointments(data.appointments || []);
      }
    } catch (err) {
      console.error('Failed to load appointments:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, selectedDate]);

  // Reset state when practice changes — including the date picker, since
  // "today" depends on the practice's timezone (PT vs CT vs ET).
  useEffect(() => {
    setEnabled(null);
    setAppointments([]);
    setLastSyncRange(null);
    setMessage(null);
    setActiveTypeFilters(new Set());
    setSelectedDate(todayInTimezone(selectedPractice?.timezone));
  }, [practiceId, selectedPractice?.timezone]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Close type dropdown on outside click
  useEffect(() => {
    if (!typeDropdownOpen) return;
    const handleClick = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [typeDropdownOpen]);

  const handleSync = async () => {
    if (!practiceId) return;
    setSyncing(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/appointments/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ practice_id: practiceId, date: selectedDate }),
      });
      const data = await response.json();
      if (response.ok && data.success && data.demo) {
        setMessage({ type: 'success', text: data.message || 'Demo schedule regenerated' });
        loadAppointments();
      } else if (response.ok && data.success) {
        setMessage({
          type: 'success',
          text: `Synced ${data.synced} appointments (${data.matched} matched to patients). Range: ${formatDate(data.range_start)} \u2013 ${formatDate(data.range_end)}`,
        });
        setLastSyncRange({ start: data.range_start, end: data.range_end });
        loadAppointments();
      } else {
        const debugInfo = data.debug_error_type ? ` [${data.debug_error_type}: ${data.debug_error_msg}]` : '';
        setMessage({ type: 'error', text: (data.error || 'Sync failed') + debugInfo });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to connect to server' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDailyEligibility = async () => {
    if (!practiceId) return;
    // Count how many patients will actually be checked (matched - recently checked)
    const matched = appointments.filter(a => a.patient_id).length;
    const unchecked = appointments.filter(a => a.patient_id && !a.eligibility).length;
    const toCheck = unchecked || matched;
    if (toCheck > 0 && !window.confirm(
      `This will run up to ${toCheck} paid eligibility check${toCheck !== 1 ? 's' : ''} via Stedi. Continue?`
    )) {
      return;
    }
    setCheckingElig(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/appointments/daily-eligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ practice_id: practiceId, date: selectedDate }),
      });
      const data = await response.json();
      if (response.ok) {
        const checked = data.checked || 0;
        const skipped = data.skipped || 0;
        const skippedMissingInfo = data.skipped_missing_info || 0;
        const eligible = data.eligible || 0;
        const unmatched = data.unmatched || 0;
        const parts = [];
        if (checked > 0) parts.push(`${checked} checked (${eligible} eligible)`);
        if (skipped > 0) parts.push(`${skipped} skipped (recent)`);
        if (skippedMissingInfo > 0) parts.push(`${skippedMissingInfo} skipped (no payer/member ID)`);
        if (unmatched > 0) parts.push(`${unmatched} not matched`);
        setMessage({ type: 'success', text: parts.join(', ') || data.message || 'Done' });
        loadAppointments();
      } else {
        setMessage({ type: 'error', text: data.error || 'Eligibility check failed' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to connect to server' });
    } finally {
      setCheckingElig(false);
    }
  };

  // The patient's outstanding claims, from the same endpoint the A/R tab uses.
  const openBalance = async (appt) => {
    setBalanceModal({ patientName: appt.patient_name, claims: null, error: null });
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/ar/patients/claims?patient_id=${appt.patient_id}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to load claims');
      const data = await response.json();
      setBalanceModal((m) => (m ? { ...m, claims: data.data || [] } : m));
    } catch (err) {
      setBalanceModal((m) => (m ? { ...m, error: err.message || 'Failed to load claims' } : m));
    }
  };

  const changeDate = (delta) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  if (!selectedPractice) {
    return (
      <div className="appointments-page">
        <div className="empty-state">
          <h3>Select a practice</h3>
          <p>Choose a practice from the sidebar to view appointments.</p>
        </div>
      </div>
    );
  }

  // Practice doesn't have scheduling integration
  if (enabled === false) {
    return (
      <div className="appointments-page">
        <div className="appointments-header">
          <h1 className="page-title">Appointments</h1>
        </div>
        <div className="not-enabled-state">
          <div className="not-enabled-icon">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <line x1="9" y1="14" x2="15" y2="20" />
              <line x1="15" y1="14" x2="9" y2="20" />
            </svg>
          </div>
          <h3>Not Yet Enabled</h3>
          <p>
            Appointment syncing isn't set up for <strong>{selectedPractice.name}</strong> yet.
          </p>
          <p className="not-enabled-sub">
            Once an EMR integration is configured in Practice Details, appointments will appear here automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="appointments-page">
      <div className="appointments-header">
        <h1 className="page-title">Appointments</h1>
        <div className="appointments-header-actions">
          <button
            className="btn-sync"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? (
              <>
                <span className="btn-spinner" />
                Syncing...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 4v6h-6" />
                  <path d="M1 20v-6h6" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
                {isDemo ? 'Regenerate demo schedule' : 'Sync with EMR'}
              </>
            )}
          </button>
          <button
            className="btn-sync btn-elig-check"
            onClick={handleDailyEligibility}
            disabled={checkingElig || syncing || isDemo}
            title={isDemo ? 'Eligibility on the demo practice is seeded, not checked live' : undefined}
          >
            {checkingElig ? (
              <>
                <span className="btn-spinner" />
                Checking...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4" />
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                </svg>
                Check Eligibility
              </>
            )}
          </button>
        </div>
      </div>

      {isDemo && demoNoticeOpen && (
        <div className="appt-message info">
          <span>
            <strong>Demo practice.</strong> Every patient here is fictional; eligibility, copays and balances are seeded, and nothing is sent to a payer.
          </span>
          <button className="message-close" onClick={() => setDemoNoticeOpen(false)}>&times;</button>
        </div>
      )}

      {message && (
        <div className={`appt-message ${message.type}`}>
          <span>{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      <div className="date-nav">
        <button className="date-nav-btn" onClick={() => changeDate(-1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </button>
        <input
          type="date"
          className="date-input"
          min={DATE_INPUT_MIN}
          max={DATE_INPUT_MAX}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <button className="date-nav-btn" onClick={() => changeDate(1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9,6 15,12 9,18" />
          </svg>
        </button>
        <button className="date-today-btn" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}>
          Today
        </button>
        {lastSyncRange && (
          <span className="sync-range-label">
            Synced: {formatDate(lastSyncRange.start)} &ndash; {formatDate(lastSyncRange.end)}
          </span>
        )}
      </div>

      {!loading && appointments.length > 0 && appointmentTypes.length > 1 && (
        <div className="type-filter-bar">
          <div className="type-filter-dropdown-wrap" ref={typeDropdownRef}>
            <button
              className="type-filter-dropdown-btn"
              onClick={() => setTypeDropdownOpen((prev) => !prev)}
            >
              {activeTypeFilters.size === 0
                ? 'All Types'
                : `${activeTypeFilters.size} Type${activeTypeFilters.size !== 1 ? 's' : ''}`}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6,9 12,15 18,9" />
              </svg>
            </button>
            {typeDropdownOpen && (
              <div className="type-filter-dropdown-menu">
                {appointmentTypes.map(([type, count]) => (
                  <label key={type} className="type-filter-option">
                    <input
                      type="checkbox"
                      checked={activeTypeFilters.has(type)}
                      onChange={() => toggleTypeFilter(type)}
                    />
                    <span className="type-filter-option-label">{type}</span>
                    <span className="type-filter-option-count">{count}</span>
                  </label>
                ))}
                {activeTypeFilters.size > 0 && (
                  <button
                    className="type-filter-clear-btn"
                    onClick={() => { setActiveTypeFilters(new Set()); setTypeDropdownOpen(false); }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
          {activeTypeFilters.size > 0 && (
            <span className="type-filter-summary">
              {filteredAppointments.length} of {appointments.length}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading appointments...</p>
        </div>
      ) : appointments.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <h3>No appointments</h3>
          <p>
            No appointments found for {formatDate(selectedDate)}.{' '}
            {isDemo
              ? 'Click "Regenerate demo schedule" to rebuild it around today.'
              : 'Click "Sync with EMR" to pull the latest schedule.'}
          </p>
        </div>
      ) : (
        <div className="appointments-table-wrap">
          <table className={`appointments-table${isDemo ? ' appointments-table-demo' : ''}`}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Patient</th>
                {isDemo && <th>Provider</th>}
                <th>Type</th>
                {isDemo && <th>Note</th>}
                {!isDemo && <th>CPTs</th>}
                {!isDemo && <th>ICD-10</th>}
                {!isDemo && <th>Status</th>}
                <th>Eligibility</th>
                <th>Copay</th>
                {isDemo && <th title="Patient-owed remainder on earlier visits">Patient Balance</th>}
                {!isDemo && <th>Note</th>}
              </tr>
            </thead>
            <tbody>
              {filteredAppointments.map((appt) => {
                const statusColor = apptStatusColors[appt.status] || '#718096';
                return (
                  <tr key={appt.id} className="appt-row">
                    <td className="appt-time">{formatTime(appt.start_time, appt.timezone)}</td>
                    <td className="appt-patient">
                      <div className="appt-patient-content">
                        <span className="patient-name">{formatPatientName(appt.patient_name)}</span>
                      </div>
                    </td>
                    {isDemo && <td className="appt-provider">{appt.provider_name || '—'}</td>}
                    <td className="appt-type">{appt.appointment_type || '\u2014'}</td>
                    {isDemo && (
                      <td className="appt-note-steps">
                        <span className="note-steps">
                          {appt.notes ? (
                            <button
                              className="note-icon-btn"
                              onClick={() => setNoteModal({ patientName: appt.patient_name, note: appt.notes })}
                              title="View progress note"
                            >
                              <NoteIcon size={20} />
                            </button>
                          ) : (
                            <span className="note-icon-muted" title="No note yet">
                              <NoteIcon size={20} />
                            </span>
                          )}
                          {appt.notes && appt.claim_created && <ClaimCheck />}
                        </span>
                      </td>
                    )}
                    {!isDemo && (
                      <>
                        <td className="appt-cpts">
                          {appt.cpt_codes ? (
                            <span className="appt-cpt-value">{appt.cpt_codes}</span>
                          ) : (
                            <span className="appt-cpt-empty">{'\u2014'}</span>
                          )}
                        </td>
                        <td className="appt-icd">
                          {appt.icd_codes ? (
                            <span className="appt-icd-value">{appt.icd_codes}</span>
                          ) : (
                            <span className="appt-icd-empty">{'\u2014'}</span>
                          )}
                        </td>
                      </>
                    )}
                    {!isDemo && (
                      <td>
                        <span className="status-badge" style={{ background: statusColor }}>
                          {appt.status || 'Unknown'}
                        </span>
                      </td>
                    )}
                    <td>
                      {isDemo && appt.eligibility ? (
                        <EligStack elig={appt.eligibility} />
                      ) : appt.is_medicare ? (
                        <div className="elig-cell">
                          <EligIcon state="na" />
                          <span className="elig-label elig-none">Medicare</span>
                        </div>
                      ) : appt.eligibility ? (
                        <div className="elig-cell">
                          <EligIcon state={appt.eligibility.icon_state} />
                          <span className="elig-label">
                            {appt.eligibility.display_label || appt.eligibility.status}
                          </span>
                          <span className="elig-payer">{appt.eligibility.payer_name}</span>
                          {appt.eligibility.checked_at && (
                            <span className="elig-date">{formatEligDate(appt.eligibility.checked_at)}</span>
                          )}
                        </div>
                      ) : appt.patient_id ? (
                        appt.skip_eligibility ? (
                          <div className="elig-cell">
                            <EligIcon state="na" />
                            <span className="elig-label elig-none">Not billed to insurance</span>
                          </div>
                        ) : appt.has_insurance_info ? (
                          <div className="elig-cell">
                            <EligIcon state="uncertain" />
                            <span className="elig-label">No check on file</span>
                          </div>
                        ) : (
                          <div className="elig-cell">
                            <EligIcon state="na" />
                            <span className="elig-label elig-none">No insurance on file</span>
                          </div>
                        )
                      ) : (
                        <span className="elig-not-matched">Not Matched</span>
                      )}
                    </td>
                    <td className="appt-copay">
                      {appt.estimated_copay != null ? (
                        <span
                          className={`copay-cell copay-${appt.estimated_copay_status === 'manual_override' ? 'manual' : 'auto'}`}
                          title={appt.estimated_copay_status === 'manual_override' ? 'Manually set' : 'Auto-derived from claim history'}
                        >
                          ${Number(appt.estimated_copay).toFixed(2)}
                          {appt.estimated_copay_status === 'manual_override' && (
                            <span className="copay-badge-manual">M</span>
                          )}
                        </span>
                      ) : (
                        <span className="copay-empty">{'—'}</span>
                      )}
                    </td>
                    {isDemo && (
                      <td className="appt-balance">
                        {appt.outstanding_balance != null ? (
                          <button
                            className="balance-link"
                            onClick={() => openBalance(appt)}
                            title="View outstanding claims"
                          >
                            ${Number(appt.outstanding_balance).toFixed(2)}
                          </button>
                        ) : (
                          <span className="copay-empty">{'—'}</span>
                        )}
                      </td>
                    )}
                    {!isDemo && (
                    <td className="appt-note">
                      {appt.notes ? (
                        <button
                          className="note-icon-btn"
                          onClick={() => setNoteModal({ patientName: appt.patient_name, note: appt.notes })}
                          title="View progress note"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10 9 9 9 8 9"/>
                          </svg>
                        </button>
                      ) : (
                        <span className="appt-note-empty">{'\u2014'}</span>
                      )}
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="appointments-summary">
            {filteredAppointments.length === appointments.length
              ? `${appointments.length} appointment${appointments.length !== 1 ? 's' : ''}`
              : `${filteredAppointments.length} of ${appointments.length} appointments`}
          </div>
        </div>
      )}

      {noteModal && (
        <div className="modal-overlay" onClick={() => setNoteModal(null)}>
          <div className="modal-content note-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Progress Note — {noteModal.patientName}</h3>
              <button className="close-btn" onClick={() => setNoteModal(null)}>&times;</button>
            </div>
            <div className="note-modal-body">
              {(() => {
                const parsed = parseFormattedNote(noteModal.note)
                  .filter((s) => s.type !== 'intro' && (s.label || s.body));
                const header = parsed.filter((s) => s.zone === 'header');
                const body = parsed.filter((s) => s.zone === 'body');
                const footer = parsed.filter((s) => s.zone === 'footer');

                const renderSection = (section, idx) => (
                  <div key={idx} className="note-section">
                    <span className="note-section-label">{section.label}:</span>{' '}
                    <span className="note-section-body">{section.body}</span>
                  </div>
                );

                return (
                  <>
                    {header.length > 0 && (
                      <div className="note-zone note-zone-header">
                        {header.map(renderSection)}
                      </div>
                    )}
                    {body.length > 0 && (
                      <div className="note-zone note-zone-body">
                        {body.map(renderSection)}
                      </div>
                    )}
                    {footer.length > 0 && (
                      <div className="note-zone note-zone-footer">
                        {footer.map(renderSection)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {balanceModal && (
        <div className="modal-overlay" onClick={() => setBalanceModal(null)}>
          <div className="modal-content note-modal balance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Outstanding Claims — {formatPatientName(balanceModal.patientName)}</h3>
              <button className="close-btn" onClick={() => setBalanceModal(null)}>&times;</button>
            </div>
            <div className="balance-modal-body">
              {balanceModal.error ? (
                <p className="balance-modal-error">{balanceModal.error}</p>
              ) : balanceModal.claims === null ? (
                <p className="balance-modal-status">Loading claims...</p>
              ) : balanceModal.claims.length === 0 ? (
                <p className="balance-modal-status">No outstanding claims.</p>
              ) : (
                <PatientARClaimsTable claims={balanceModal.claims} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppointmentsPage;
